import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CheckCircle, AlertTriangle, Mail, Send, Eye, StopCircle, Users, RefreshCw, Search,
} from 'lucide-react';
import api from '../../api';
import Avatar from '../../components/Avatar';
import Spinner from '../../components/Spinner';
import TagPicker from '../../components/TagPicker';
import HtmlEditor from '../../components/HtmlEditor';
import MailSendersSection from './MailSendersSection';
import MailClientsSection from './MailClientsSection';

const inputClass = "px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10";
const primaryBtn = "bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium transition-colors flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed";
const secondaryBtn = "bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium transition-all flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-gray-50 enabled:hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed";
const cardClass = "bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200";

const STATUS_LABELS = {
    PREPARING: 'Собираем получателей',
    SENDING: 'Отправляется',
    COMPLETED: 'Завершена',
    CANCELLED: 'Остановлена',
};

const STATUS_STYLES = {
    PREPARING: 'bg-amber-50 text-amber-800 border-amber-200',
    SENDING: 'bg-blue-50 text-blue-800 border-blue-200',
    COMPLETED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    CANCELLED: 'bg-gray-100 text-gray-600 border-gray-200',
};

const roleLabel = (role) => role.replace(/^ROLE_/, '');

const formatMoment = (value) => (value ? new Date(value).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

/** Поля выбранного шаблона: форму строит сервер, здесь только отрисовка по описанию. */
const TemplateFields = ({ template, values, onChange, unsubscribe }) => {
    if (!template) return null;
    if (template.fields.length === 0) {
        return (
            <p className="text-sm text-gray-500">
                У этого шаблона нет полей: его содержимое подставляет сам сервис.
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {template.fields.map(field => (
                <div key={field.name} className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-700">
                        {field.label}
                        {field.required && <span className="text-red-500"> *</span>}
                        {field.kind === 'LIST' && <span className="text-gray-400 font-normal"> — по одному пункту на строку</span>}
                    </label>
                    {field.kind === 'HTML' ? (
                        <>
                            <p className="text-[13px] text-gray-500 m-0">
                                Вставьте HTML или загрузите файл до 512 КБ. Поддерживаются полные документы со своими стилями.
                                Для персонализации используйте {'{{name}}'}, {'{{email}}'}, {'{{portalUrl}}'}, {'{{profileUrl}}'} и {'{{securityUrl}}'}.
                            </p>
                            <HtmlEditor
                                value={values[field.name] ?? ''}
                                onChange={value => onChange(field.name, value)}
                                variables={[
                                    { name: 'name', label: 'Имя пользователя', sample: 'Иван' },
                                    { name: 'email', label: 'Email', sample: 'ivan@example.com' },
                                    { name: 'portalUrl', label: 'Личный кабинет', sample: 'https://accounts.example.com' },
                                    { name: 'profileUrl', label: 'Профиль', sample: '/dashboard/profile' },
                                    { name: 'securityUrl', label: 'Безопасность', sample: '/dashboard/security' },
                                    ...(unsubscribe ? [{ name: 'unsubscribeUrl', label: 'Отписаться / отозвать согласие', sample: 'Персональная ссылка отписки' }] : []),
                                ]}
                                allowVisual={false}
                                allowPreview={false}
                            />
                        </>
                    ) : field.kind === 'MULTILINE' || field.kind === 'LIST' ? (
                        <textarea
                            className={`${inputClass} min-h-[110px] resize-y font-normal`}
                            value={values[field.name] ?? ''}
                            placeholder={field.sample}
                            onChange={e => onChange(field.name, e.target.value)}
                        />
                    ) : (
                        <input
                            className={inputClass}
                            type={field.kind === 'URL' ? 'url' : 'text'}
                            value={values[field.name] ?? ''}
                            placeholder={field.sample}
                            onChange={e => onChange(field.name, e.target.value)}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

/**
 * Готовое письмо в рамке.
 *
 * <p>Именно iframe, а не вставка разметки в страницу: у письма свои стили на весь документ,
 * и вставленное в панель оно перекрасило бы саму панель. Плюс sandbox - содержимое пишет
 * человек, и выполнять из него скрипты в админке незачем.
 */
const PreviewFrame = ({ html, loading }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center gap-3 text-gray-500 h-[420px] border border-gray-200 rounded-lg bg-gray-50">
                <Spinner size={22} /> Собираем письмо…
            </div>
        );
    }
    if (!html) {
        return (
            <div className="flex items-center justify-center text-gray-400 text-sm h-[420px] border border-dashed border-gray-300 rounded-lg bg-gray-50 text-center px-6">
                Нажмите «Предпросмотр», чтобы увидеть письмо целиком — так, как его получит адресат.
            </div>
        );
    }
    return (
        <iframe
            title="Предпросмотр письма"
            srcDoc={html}
            sandbox=""
            className="w-full h-[420px] border border-gray-200 rounded-lg bg-white"
        />
    );
};

const AdminMailPage = () => {
    const [tab, setTab] = useState('campaigns'); // 'campaigns' | 'personal'
    const [templates, setTemplates] = useState([]);
    const [roles, setRoles] = useState([]);
    const [alert, setAlert] = useState(null);

    // Общая для обеих вкладок часть письма
    const [templateId, setTemplateId] = useState('');
    const [subject, setSubject] = useState('');
    const [values, setValues] = useState({});

    // Рассылка
    const [campaignName, setCampaignName] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [agreements, setAgreements] = useState([]);
    const [selectedAgreements, setSelectedAgreements] = useState([]);
    const [recipientMode, setRecipientMode] = useState('filters');
    const [recipients, setRecipients] = useState('');
    const [audienceError, setAudienceError] = useState(null);
    const [senders, setSenders] = useState([]);
    const [mailClients, setMailClients] = useState([]);
    // Пусто - письмо уходит с системного адреса сервиса авторизации.
    const [senderId, setSenderId] = useState('');
    const [audience, setAudience] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [starting, setStarting] = useState(false);
    const [testing, setTesting] = useState(false);

    // Письмо пользователю
    const [search, setSearch] = useState('');
    const [foundUsers, setFoundUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [sendingPersonal, setSendingPersonal] = useState(false);

    // Предпросмотр
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const previewRequest = useRef(0);

    // Старое письмо не должно оставаться превью новой разметки, даже если его запрос
    // завершился позже: администратор мог уже сменить шаблон или загрузить другой файл.
    useEffect(() => {
        previewRequest.current += 1;
        setPreview(null);
        setPreviewLoading(false);
    }, [templateId, subject, values]);

    // Настройки письма нужны только там, где письмо составляют: на вкладках отправителей и
    // клиентов они были бы лишним шумом.
    const isComposing = tab === 'campaigns' || tab === 'personal';

    const campaignTemplates = useMemo(() => templates.filter(t => t.campaign), [templates]);
    const template = useMemo(() => templates.find(t => t.id === templateId) || null, [templates, templateId]);

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 6000);
    };

    const fetchSenders = useCallback(() => {
        api.get('/api/admin/mail/senders')
            .then(res => setSenders(res.data))
            .catch(() => setSenders([]));
    }, []);

    const fetchMailClients = useCallback(() => {
        api.get('/api/admin/mail/clients')
            .then(res => setMailClients(res.data))
            .catch(() => setMailClients([]));
    }, []);

    const fetchCampaigns = useCallback(() => {
        api.get('/api/admin/mail/campaigns')
            .then(res => setCampaigns(res.data))
            .catch(() => setCampaigns([]));
    }, []);

    useEffect(() => {
        api.get('/api/admin/mail/templates')
            .then(res => {
                setTemplates(res.data);
                const first = res.data.find(t => t.campaign);
                if (first) {
                    setTemplateId(first.id);
                    setSubject(first.defaultSubject || '');
                }
            })
            .catch(() => showAlert('error', 'Не удалось загрузить шаблоны писем'));

        api.get('/api/admin/users/roles')
            .then(res => setRoles(res.data))
            .catch(() => setRoles([]));

        api.get('/api/admin/tags')
            .then(res => setTags(res.data))
            .catch(() => setTags([]));
        api.get('/api/admin/agreements').then(res => setAgreements(res.data))
            .catch(() => showAlert('error', 'Не удалось загрузить соглашения для фильтрации'));

        fetchCampaigns();
        fetchSenders();
        fetchMailClients();
    }, [fetchCampaigns, fetchSenders, fetchMailClients]);

    // Пока есть незавершённые рассылки, счётчики обновляются сами: отправка идёт в фоне,
    // и требовать перезагрузки страницы, чтобы увидеть ход дела, незачем.
    useEffect(() => {
        const running = campaigns.some(c => c.status === 'PREPARING' || c.status === 'SENDING');
        if (!running) return undefined;
        const timer = setInterval(fetchCampaigns, 5000);
        return () => clearInterval(timer);
    }, [campaigns, fetchCampaigns]);

    useEffect(() => {
        let active = true;
        setAudience(null);
        setAudienceError(null);
        const timer = setTimeout(() => {
            const request = recipientMode === 'list'
                ? api.post('/api/admin/mail/audience', { roles: selectedRoles, tags: selectedTags, agreements: selectedAgreements, recipients })
                : api.get('/api/admin/mail/audience', { params: { roles: selectedRoles, tags: selectedTags, agreements: selectedAgreements } });
            request.then(res => { if (active) setAudience(res.data.count); })
                .catch(error => { if (active) setAudienceError(error.response?.data?.detail || error.response?.data?.message || 'Не удалось проверить получателей'); });
        }, 350);
        return () => { active = false; clearTimeout(timer); };
    }, [selectedRoles, selectedTags, selectedAgreements, recipientMode, recipients]);

    const handleTemplateChange = (id) => {
        setTemplateId(id);
        setPreview(null);
        const chosen = templates.find(t => t.id === id);
        setValues(Object.fromEntries((chosen?.fields || [])
            .filter(field => field.kind === 'HTML')
            .map(field => [field.name, field.sample || ''])));
        if (chosen && (!subject || templates.some(t => t.defaultSubject === subject))) {
            setSubject(chosen.defaultSubject || '');
        }
    };

    const handleFieldChange = (name, value) => {
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const toggleRole = (role) => {
        setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
    };

    const selectedAgreement = tab === 'campaigns' ? agreements.find(document => document.document_id === selectedAgreements[0]) : null;
    useEffect(() => { previewRequest.current++; setPreview(null); }, [selectedAgreement?.document_id, selectedAgreement?.advertising]);
    const handlePreview = () => {
        const requestId = ++previewRequest.current;
        setPreview(null);
        setPreviewLoading(true);
        api.post('/api/admin/mail/preview', { templateId, subject, data: values, agreementId: selectedAgreement?.document_id || null })
            .then(res => { if (requestId === previewRequest.current) setPreview(res.data); })
            .catch(err => {
                if (requestId !== previewRequest.current) return;
                setPreview(null);
                showAlert('error', err.response?.data?.message || 'Не удалось собрать письмо');
            })
            .finally(() => { if (requestId === previewRequest.current) setPreviewLoading(false); });
    };

    const handleSendTest = () => {
        setTesting(true);
        api.post('/api/admin/mail/campaigns/test', {
            templateId, subject, data: values, senderId: senderId === '' ? null : Number(senderId), agreementId: selectedAgreement?.document_id || null,
        })
            .then(() => showAlert('success', 'Проверочное письмо отправлено на вашу почту'))
            .catch(err => showAlert('error', err.response?.data?.message || 'Не удалось отправить письмо'))
            .finally(() => setTesting(false));
    };

    const handleStartCampaign = () => {
        if (!window.confirm(audience === null
            ? 'Запустить рассылку?'
            : `Письмо получат ${audience} чел. Запустить рассылку?`)) return;

        setStarting(true);
        api.post('/api/admin/mail/campaigns', {
            name: campaignName, subject, templateId, data: values,
            roles: selectedRoles, tags: selectedTags,
            agreements: selectedAgreements, recipients: recipientMode === 'list' ? recipients : null,
            senderId: senderId === '' ? null : Number(senderId),
        })
            .then(() => {
                showAlert('success', 'Рассылка запущена. Получатели собираются в фоне.');
                setCampaignName('');
                fetchCampaigns();
            })
            .catch(err => showAlert('error', err.response?.data?.message || 'Не удалось запустить рассылку'))
            .finally(() => setStarting(false));
    };

    const handleCancelCampaign = (id) => {
        if (!window.confirm('Остановить рассылку? Уже отправленные письма вернуть нельзя.')) return;

        api.post(`/api/admin/mail/campaigns/${id}/cancel`)
            .then(() => {
                showAlert('success', 'Рассылка остановлена');
                fetchCampaigns();
            })
            .catch(err => showAlert('error', err.response?.data?.message || 'Не удалось остановить рассылку'));
    };

    const handleSearchUsers = (e) => {
        e.preventDefault();
        api.get('/api/admin/users', { params: { page: 0, size: 10, search } })
            .then(res => setFoundUsers(res.data.content))
            .catch(() => setFoundUsers([]));
    };

    const handleSendPersonal = () => {
        if (!selectedUser) return;
        setSendingPersonal(true);
        api.post('/api/admin/mail/send', {
            userId: selectedUser.id, subject, templateId, data: values,
            senderId: senderId === '' ? null : Number(senderId),
        })
            .then(res => showAlert('success', `Письмо отправлено на ${res.data.email}`))
            .catch(err => showAlert('error', err.response?.data?.message || 'Не удалось отправить письмо'))
            .finally(() => setSendingPersonal(false));
    };

    const tabButton = (id, label) => (
        <button
            key={id}
            type="button"
            onClick={() => { setTab(id); setPreview(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all border ${
                tab === id
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
        >
            {label}
        </button>
    );

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-[28px] font-semibold text-gray-900 m-0">Почта</h1>
                <p className="text-gray-500 text-sm mt-2">
                    Рассылки по ролям и письма отдельным пользователям. Отправка идёт в фоне:
                    страницу можно закрыть.
                </p>
            </div>

            {alert && (
                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${alert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {alert.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    <span>{alert.message}</span>
                </div>
            )}

            <div className="flex gap-2 mb-6 flex-wrap">
                {tabButton('campaigns', 'Рассылка')}
                {tabButton('personal', 'Письмо пользователю')}
                {tabButton('senders', 'Отправители')}
                {tabButton('clients', 'Клиенты API')}
            </div>

            {tab === 'senders' && (
                <MailSendersSection senders={senders} onChanged={fetchSenders} onAlert={showAlert} />
            )}

            {tab === 'clients' && (
                <MailClientsSection
                    clients={mailClients}
                    senders={senders}
                    onChanged={() => { fetchMailClients(); fetchSenders(); }}
                    onAlert={showAlert}
                />
            )}

            {isComposing && (
            <>
            <div className={cardClass}>
                <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Письмо</h3>

                <div className="mb-4 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-700">От кого</label>
                    <select className={inputClass} value={senderId} onChange={e => setSenderId(e.target.value)}>
                        <option value="">Системный адрес сервиса</option>
                        {senders.filter(sender => sender.enabled).map(sender => (
                            <option key={sender.id} value={sender.id}>
                                {sender.name} ({sender.fromEmail})
                            </option>
                        ))}
                    </select>
                    <span className="text-[13px] text-gray-500">
                        Адреса сервисов заводятся на вкладке «Отправители».
                    </span>
                </div>


                <div className="mb-4 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-700">Шаблон</label>
                    <div className="flex flex-col gap-2">
                        {campaignTemplates.map(item => (
                            <label
                                key={item.id}
                                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                                    templateId === item.id
                                        ? 'border-blue-300 bg-blue-50/50'
                                        : 'border-gray-200 hover:border-gray-300'}`}
                            >
                                <input
                                    type="radio"
                                    name="template"
                                    className="mt-1"
                                    checked={templateId === item.id}
                                    onChange={() => handleTemplateChange(item.id)}
                                />
                                <span>
                                    <span className="block text-sm font-medium text-gray-900">{item.name}</span>
                                    <span className="block text-[13px] text-gray-500">{item.description}</span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="mb-4 flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-gray-700">Тема письма</label>
                    <input className={inputClass} value={subject} onChange={e => setSubject(e.target.value)} />
                </div>

                <TemplateFields template={template} values={values} onChange={handleFieldChange} unsubscribe={!!selectedAgreement?.advertising} />
            </div>

            <div className={cardClass}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="m-0 text-lg font-semibold text-gray-900">Предпросмотр</h3>
                    <button type="button" className={secondaryBtn} onClick={handlePreview} disabled={!templateId}>
                        <Eye size={16} /> Предпросмотр
                    </button>
                </div>
                <PreviewFrame html={preview} loading={previewLoading} />
                <p className="text-[13px] text-gray-500 mt-3 mb-0">
                    В превью используются данные вашего аккаунта. При отправке подставляются данные каждого получателя.
                </p>
            </div>
            </>
            )}

            {tab === 'campaigns' ? (
                <>
                    <div className={cardClass}>
                        <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Получатели</h3>

                        <label className="flex flex-col gap-2 mb-4 text-sm">Источник получателей
                            <select className={inputClass} value={recipientMode} onChange={event => setRecipientMode(event.target.value)}>
                                <option value="filters">Пользователи аккаунтов</option><option value="list">Список email / userId</option>
                            </select>
                        </label>
                        {recipientMode === 'list' && <div className="flex flex-col gap-2 mb-5">
                            <label className="text-sm">Email или userId<textarea aria-label="Список получателей" className={`${inputClass} block w-full min-h-[120px] mt-2`} placeholder={'user@example.com, 123\nother@example.com'} value={recipients} onChange={event => setRecipients(event.target.value)} /></label>
                            <label className="text-sm">Загрузить CSV <input type="file" accept=".csv,text/csv,text/plain" onChange={async event => {
                                const file = event.target.files?.[0];
                                if (!file) return;
                                try {
                                    if (file.size > 1_000_000) throw new Error('CSV должен быть не больше 1 МБ');
                                    setRecipients(await file.text());
                                } catch (error) { showAlert('error', error.message || 'Не удалось прочитать CSV'); }
                                event.target.value = '';
                            }} /></label>
                            <p className="text-xs text-gray-500">До 50 000 значений. Разделители: запятая, точка с запятой, пробел или новая строка. CSV может содержать столбцы email и userId. Повторы удаляются. Все фильтры ниже применяются также к списку.</p>
                        </div>}

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Роли</label>
                            <p className="text-[13px] text-gray-500 m-0 mb-1">
                                Не выбрано — без ограничения по ролям. Фильтры объединяются через «и».
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {roles.map(role => (
                                    <button
                                        key={role}
                                        type="button"
                                        onClick={() => toggleRole(role)}
                                        className={`px-3 py-1.5 rounded-full text-[13px] font-medium border cursor-pointer transition-all ${
                                            selectedRoles.includes(role)
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                                    >
                                        {roleLabel(role)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Метки</label>
                            <p className="text-[13px] text-gray-500 m-0 mb-1">
                                Чем человек пользуется. Метка появляется у него сама при входе в сервис
                                и держится год с последнего входа. С ролями складывается через «и».
                            </p>
                            <TagPicker
                                tags={tags}
                                selected={selectedTags}
                                onChange={setSelectedTags}
                                emptyHint="Меток пока нет — их заводят на странице OAuth2-клиентов."
                            />
                        </div>

                        <fieldset className="mb-5"><legend className="text-sm font-medium mb-2">Принятые соглашения</legend>
                            <p className="text-xs text-gray-500 mb-3">Можно выбрать одно соглашение. Только принявшие его текущую версию, без отзыва. Email без связанного аккаунта не проходит этот фильтр.</p>
                            <div className="flex flex-col gap-2">{agreements.map(document => <label key={document.document_id} className="flex items-start gap-2 text-sm">
                                <input type="checkbox" checked={selectedAgreements.includes(document.document_id)} onChange={event => setSelectedAgreements(event.target.checked ? [document.document_id] : [])} />
                                <span>{document.title}{document.advertising ? ' · Рекламное' : ''} <span className="text-gray-500">· версия {document.version}</span></span>
                            </label>)}</div>
                        </fieldset>
                        {selectedAgreement?.advertising && <p className="text-sm text-gray-500 mb-4">Доступен плейсхолдер {'{{unsubscribeUrl}}'} — персональная ссылка отзыва согласия. В готовые шаблоны ссылка отписки добавляется автоматически; в свой HTML можно вставить её кнопкой редактора. В превью и письме себе ссылка демонстрационная.</p>}
                        {audienceError && <p role="alert" className="text-sm text-red-600">{audienceError}</p>}
                        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5">
                            <Users size={16} className="text-gray-400" />
                            {audience === null
                                ? audienceError ? 'Проверьте список и фильтры' : 'Считаем получателей…'
                                : <span>Письмо получат <span className="font-semibold text-gray-900">{audience}</span> чел.</span>}
                        </div>

                        <div className="mt-4 flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-gray-700">Название рассылки</label>
                            <p className="text-[13px] text-gray-500 m-0 mb-1">
                                Видно только в этом списке — получателю не показывается.
                            </p>
                            <input
                                className={inputClass}
                                value={campaignName}
                                placeholder="Например: работы 25 августа"
                                onChange={e => setCampaignName(e.target.value)}
                            />
                        </div>

                        <div className="flex gap-3 mt-5 flex-wrap">
                            <button type="button" className={primaryBtn} onClick={handleStartCampaign}
                                    disabled={starting || !templateId || !subject || audience === null || audience === 0 || !!audienceError}>
                                {starting ? <Spinner size={16} /> : <Send size={16} />}
                                {starting ? 'Запускаем…' : 'Запустить рассылку'}
                            </button>
                            <button type="button" className={secondaryBtn} onClick={handleSendTest}
                                    disabled={testing || !templateId || !subject}>
                                {testing ? <Spinner size={16} /> : <Mail size={16} />}
                                Отправить себе
                            </button>
                        </div>
                    </div>

                    <div className={cardClass}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="m-0 text-lg font-semibold text-gray-900">Рассылки</h3>
                            <button type="button" className={secondaryBtn} onClick={fetchCampaigns}>
                                <RefreshCw size={16} /> Обновить
                            </button>
                        </div>

                        {campaigns.length === 0 ? (
                            <p className="text-sm text-gray-500 m-0">Рассылок пока не было.</p>
                        ) : (
                            <ul className="list-none p-0 m-0">
                                {campaigns.map(campaign => (
                                    <li key={campaign.id} className="py-4 border-b border-gray-100 last:border-b-0">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div className="font-medium text-gray-900 truncate">{campaign.name}</div>
                                                <div className="text-[13px] text-gray-500 truncate">
                                                    {campaign.subject} · {campaign.templateName}
                                                </div>
                                                <div className="text-xs text-gray-400 mt-1">
                                                    {formatMoment(campaign.createdAt)}
                                                    {campaign.roles.length > 0 && ` · роли: ${campaign.roles.map(roleLabel).join(', ')}`}
                                                    {campaign.tags?.length > 0 && ` · метки: ${campaign.tags.join(', ')}`}
                                                    {campaign.senderEmail && ` · от ${campaign.senderEmail}`}
                                                    {campaign.roles.length === 0 && !campaign.tags?.length && ' · все пользователи'}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_STYLES[campaign.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                    {STATUS_LABELS[campaign.status] || campaign.status}
                                                </span>
                                                {(campaign.status === 'PREPARING' || campaign.status === 'SENDING') && (
                                                    <button
                                                        type="button"
                                                        title="Остановить рассылку"
                                                        onClick={() => handleCancelCampaign(campaign.id)}
                                                        className="bg-transparent text-gray-400 p-2 border border-transparent rounded-md cursor-pointer transition-all hover:text-red-600 hover:border-red-200 hover:bg-red-50"
                                                    >
                                                        <StopCircle size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[13px] text-gray-500">
                                            <span>всего: <span className="text-gray-900 font-medium">{campaign.totalCount}</span></span>
                                            <span>отправлено: <span className="text-emerald-700 font-medium">{campaign.sentCount}</span></span>
                                            {campaign.pendingCount > 0 && <span>в очереди: <span className="text-gray-900 font-medium">{campaign.pendingCount}</span></span>}
                                            {campaign.failedCount > 0 && <span>ошибок: <span className="text-red-600 font-medium">{campaign.failedCount}</span></span>}
                                            {campaign.cancelledCount > 0 && <span>отменено: <span className="text-gray-900 font-medium">{campaign.cancelledCount}</span></span>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            ) : (
                <div className={cardClass}>
                    <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Кому отправить</h3>

                    <form onSubmit={handleSearchUsers} className="flex gap-3 mb-4">
                        <input
                            className={`${inputClass} flex-1`}
                            value={search}
                            placeholder="Имя или адрес почты"
                            onChange={e => setSearch(e.target.value)}
                        />
                        <button type="submit" className={secondaryBtn}>
                            <Search size={16} /> Найти
                        </button>
                    </form>

                    {foundUsers.length > 0 && (
                        <ul className="list-none p-0 m-0 mb-4 border border-gray-200 rounded-lg overflow-hidden">
                            {foundUsers.map(user => (
                                <li key={user.id}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedUser(user)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                                            selectedUser?.id === user.id ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
                                    >
                                        <Avatar src={user.avatarUrl} name={user.name} email={user.email} size={32} />
                                        <span className="flex flex-col overflow-hidden">
                                            <span className="text-sm font-medium text-gray-900 truncate">{user.name || 'Без имени'}</span>
                                            <span className="text-xs text-gray-500 truncate">{user.email}</span>
                                        </span>
                                        {selectedUser?.id === user.id && <CheckCircle size={16} className="text-blue-600 ml-auto shrink-0" />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    {selectedUser && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 mb-4">
                            <Mail size={16} className="text-gray-400" />
                            Получатель: <span className="font-medium text-gray-900">{selectedUser.email}</span>
                        </div>
                    )}

                    <button
                        type="button"
                        className={primaryBtn}
                        onClick={handleSendPersonal}
                        disabled={sendingPersonal || !selectedUser || !templateId || !subject}
                    >
                        {sendingPersonal ? <Spinner size={16} /> : <Send size={16} />}
                        {sendingPersonal ? 'Отправляем…' : 'Отправить письмо'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AdminMailPage;
